import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { TowerIcon } from './TowerIcon';

const navItems = [
  { label: 'Home', to: '/#home', target: 'home' },
  { label: 'About Us', to: '/#about-us', target: 'about-us' },
  { label: 'Our Services', to: '/#services', target: 'services' },
  { label: 'How We Work', to: '/#how-we-work', target: 'how-we-work' },
  { label: 'Why Choose Us', to: '/#why-choose-us', target: 'why-choose-us' },
  { label: 'Industries We Support', to: '/#industries', target: 'industries' },
  { label: 'Careers', to: '/careers', target: 'careers', page: true },
];

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (e, targetId) => {
    setIsMobileMenuOpen(false);
    // "Page" nav items (no anchor) navigate normally — let Link handle it.
    if (!targetId) {
      return;
    }
    // On homepage, prevent default and smooth scroll
    if (location.pathname === '/' && targetId) {
      e.preventDefault();
      const el = document.getElementById(targetId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        window.history.replaceState(null, '', `/#${targetId}`);
      }
    }
    // On legal pages, let Link default navigate to /#target (ScrollToHash handles the scroll)
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-white/95 backdrop-blur-md'
      }`}
    >
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <div className="flex items-center">
            <Link
              to="/"
              onClick={(e) => handleNavClick(e, 'home')}
              className="flex items-center space-x-3 hover:opacity-90 transition-opacity duration-300 group py-2"
              data-testid="header-logo"
              aria-label="Tower Technology home"
            >
              <TowerIcon size={40} />
              <span className="text-xl md:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent group-hover:from-blue-700 group-hover:to-blue-900 transition-all duration-300 leading-tight pb-1 whitespace-nowrap">
                Tower Technology
              </span>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <Button
                key={item.target}
                asChild
                variant="ghost"
                className="text-gray-700 hover:text-blue-700 hover:bg-blue-50 transition-colors text-sm"
              >
                <Link
                  to={item.to}
                  onClick={(e) => handleNavClick(e, item.page ? null : item.target)}
                  data-testid={`nav-${item.target}`}
                >
                  {item.label}
                </Link>
              </Button>
            ))}
            <Button
              asChild
              className="ml-3 bg-blue-700 hover:bg-blue-800 text-white transition-colors"
            >
              <Link
                to="/#contact"
                onClick={(e) => handleNavClick(e, 'contact')}
                data-testid="nav-discuss-project"
              >
                Discuss Your Project
              </Link>
            </Button>
          </nav>

          {/* Mobile Menu Button */}
          <button
            type="button"
            aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMobileMenuOpen}
            className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? (
              <X className="w-6 h-6 text-gray-700" />
            ) : (
              <Menu className="w-6 h-6 text-gray-700" />
            )}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div
            className="lg:hidden py-4 border-t border-gray-200 bg-white/95 backdrop-blur-md"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col space-y-1">
              {navItems.map((item) => (
                <Button
                  key={item.target}
                  asChild
                  variant="ghost"
                  className="text-gray-700 hover:text-blue-700 hover:bg-blue-50 justify-start"
                >
                  <Link
                    to={item.to}
                    onClick={(e) => handleNavClick(e, item.page ? null : item.target)}
                    data-testid={`mobile-nav-${item.target}`}
                  >
                    {item.label}
                  </Link>
                </Button>
              ))}
              <Button
                asChild
                className="bg-blue-700 hover:bg-blue-800 text-white mt-2"
              >
                <Link
                  to="/#contact"
                  onClick={(e) => handleNavClick(e, 'contact')}
                  data-testid="mobile-nav-discuss-project"
                >
                  Discuss Your Project
                </Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};
